import { useState, useRef } from "react";
import { Plus, Trash2, Wand2, Image as ImageIcon } from "lucide-react";
import { api, imageUrl } from "@/lib/api";

const COMMON_ATTRIBUTES = [
    "color",
    "size",
    "weight",
    "length",
    "model",
    "spool_size",
    "hook_size",
    "lb_strength",
    "action",
    "power",
    "gear_ratio",
    "handedness",
    "pack_size",
];

let _local = 0;
const nextLocalId = () => `local-${Date.now()}-${++_local}`;

export default function VariantEditor({
    attributesSchema,
    setAttributesSchema,
    variants,
    setVariants,
    basePrice,
}) {
    const [newAttr, setNewAttr] = useState("");

    const addAttribute = (key) => {
        const k = (key || newAttr).trim();
        if (!k) return;
        if (attributesSchema.includes(k)) return;
        setAttributesSchema([...attributesSchema, k]);
        setNewAttr("");
    };

    const removeAttribute = (key) => {
        setAttributesSchema(attributesSchema.filter((a) => a !== key));
        setVariants(
            variants.map((v) => {
                const next = { ...v, attributes: { ...v.attributes } };
                delete next.attributes[key];
                return next;
            })
        );
    };

    const addVariant = () => {
        const blank = {
            id: nextLocalId(),
            sku: "",
            label: "",
            attributes: Object.fromEntries(attributesSchema.map((k) => [k, ""])),
            price: basePrice || 0,
            sale_price: null,
            stock: 0,
            shipping_weight_g: null,
            image_url: "",
            active: true,
        };
        setVariants([...variants, blank]);
    };

    const updateVariant = (idx, field, value) => {
        setVariants(
            variants.map((v, i) => {
                if (i !== idx) return v;
                if (field.startsWith("attr.")) {
                    const key = field.slice(5);
                    const next = { ...v, attributes: { ...v.attributes, [key]: value } };
                    next.label = attributesSchema
                        .map((k) => next.attributes[k])
                        .filter(Boolean)
                        .join(" / ");
                    return next;
                }
                return { ...v, [field]: value };
            })
        );
    };

    const removeVariant = (idx) => {
        setVariants(variants.filter((_, i) => i !== idx));
    };

    const generateCombinations = () => {
        if (attributesSchema.length === 0) return;
        // Collect existing options per attribute
        const optionsPerAttr = attributesSchema.map((k) => {
            const set = new Set();
            variants.forEach((v) => {
                if (v.attributes?.[k]) set.add(v.attributes[k]);
            });
            return Array.from(set);
        });
        if (optionsPerAttr.some((arr) => arr.length === 0)) {
            alert("Add at least one value for each attribute (in existing variants) before generating combinations.");
            return;
        }
        // Cartesian product
        const cartesian = (arrs) =>
            arrs.reduce((acc, curr) => acc.flatMap((a) => curr.map((c) => [...a, c])), [[]]);
        const combos = cartesian(optionsPerAttr);
        const existing = new Set(
            variants.map((v) => attributesSchema.map((k) => v.attributes?.[k] || "").join("|"))
        );
        const additions = combos
            .filter((combo) => !existing.has(combo.join("|")))
            .map((combo) => {
                const attrs = Object.fromEntries(attributesSchema.map((k, i) => [k, combo[i]]));
                return {
                    id: nextLocalId(),
                    sku: "",
                    label: combo.filter(Boolean).join(" / "),
                    attributes: attrs,
                    price: basePrice || 0,
                    sale_price: null,
                    stock: 0,
                    shipping_weight_g: null,
                    image_url: "",
                    active: true,
                };
            });
        setVariants([...variants, ...additions]);
    };

    return (
        <div className="space-y-4" data-testid="variant-editor">
            <div className="border border-[#27272A] bg-[#0A0A0B] rounded-sm p-4">
                <div className="text-xs uppercase tracking-widest font-display font-bold text-white/65 mb-2">
                    Variant Attributes
                </div>
                <div className="flex flex-wrap gap-2 mb-3">
                    {attributesSchema.map((a) => (
                        <span
                            key={a}
                            className="inline-flex items-center gap-2 px-2.5 py-1 border border-[#00BCE5]/40 bg-[#00BCE5]/5 text-[#00BCE5] text-xs font-display uppercase tracking-widest rounded-sm"
                        >
                            {a.replace("_", " ")}
                            <button
                                type="button"
                                className="hover:text-[#FF3B30]"
                                onClick={() => removeAttribute(a)}
                                aria-label={`Remove ${a}`}
                            >
                                <Trash2 size={12} />
                            </button>
                        </span>
                    ))}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <select
                        value=""
                        onChange={(e) => addAttribute(e.target.value)}
                        className="bg-[#121214] border border-[#27272A] rounded-sm px-2 py-1.5 text-sm focus:border-[#00BCE5] outline-none"
                        data-testid="variant-attr-select"
                    >
                        <option value="">+ Add common…</option>
                        {COMMON_ATTRIBUTES.filter((a) => !attributesSchema.includes(a)).map((a) => (
                            <option key={a} value={a}>{a.replace("_", " ")}</option>
                        ))}
                    </select>
                    <input
                        value={newAttr}
                        onChange={(e) => setNewAttr(e.target.value)}
                        placeholder="custom attribute"
                        className="bg-[#121214] border border-[#27272A] rounded-sm px-2 py-1.5 text-sm focus:border-[#00BCE5] outline-none"
                        data-testid="variant-attr-custom-input"
                    />
                    <button
                        type="button"
                        onClick={() => addAttribute(newAttr)}
                        className="bg-[#27272A] text-white px-3 py-1.5 rounded-sm text-sm font-display uppercase tracking-widest font-bold hover:bg-[#00BCE5] hover:text-black"
                        data-testid="variant-attr-add"
                    >
                        Add
                    </button>
                </div>
            </div>

            <div className="flex items-center justify-between">
                <h4 className="font-display text-sm uppercase tracking-widest font-bold text-white/65">
                    Variants ({variants.length})
                </h4>
                <div className="flex gap-2">
                    {attributesSchema.length > 0 && variants.length > 0 && (
                        <button
                            type="button"
                            onClick={generateCombinations}
                            className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 border border-[#00BCE5]/40 text-[#00BCE5] rounded-sm uppercase font-display tracking-widest font-bold hover:bg-[#00BCE5]/10"
                            data-testid="variant-generate-combos"
                        >
                            <Wand2 size={12} /> Generate combinations
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={addVariant}
                        className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 bg-[#00BCE5] text-black rounded-sm uppercase font-display tracking-widest font-bold hover:bg-[#33C9EA]"
                        data-testid="variant-add-row"
                    >
                        <Plus size={12} /> Add variant
                    </button>
                </div>
            </div>

            {variants.length === 0 ? (
                <div className="text-sm text-white/45 border border-dashed border-[#27272A] p-5 rounded-sm">
                    No variants yet. Add attribute(s) and create variants.
                </div>
            ) : (
                <div className="overflow-x-auto border border-[#27272A] rounded-sm">
                    <table className="w-full text-sm">
                        <thead className="bg-[#121214] text-xs uppercase tracking-widest font-display text-white/55">
                            <tr>
                                {attributesSchema.map((a) => (
                                    <th key={a} className="px-2 py-2 text-left">{a.replace("_", " ")}</th>
                                ))}
                                <th className="px-2 py-2 text-left">SKU</th>
                                <th className="px-2 py-2 text-left">Price</th>
                                <th className="px-2 py-2 text-left">Sale</th>
                                <th className="px-2 py-2 text-left">Stock</th>
                                <th className="px-2 py-2 text-left">Active</th>
                                <th className="px-2 py-2 text-left">Image</th>
                                <th className="px-2 py-2"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {variants.map((v, idx) => (
                                <tr key={v.id} className="border-t border-[#27272A]" data-testid={`variant-row-${idx}`}>
                                    {attributesSchema.map((a) => (
                                        <td key={a} className="px-2 py-1.5">
                                            <input
                                                value={v.attributes?.[a] || ""}
                                                onChange={(e) => updateVariant(idx, `attr.${a}`, e.target.value)}
                                                className="w-28 bg-[#0A0A0B] border border-[#27272A] rounded-sm px-2 py-1 focus:border-[#00BCE5] outline-none"
                                            />
                                        </td>
                                    ))}
                                    <td className="px-2 py-1.5">
                                        <input
                                            value={v.sku}
                                            onChange={(e) => updateVariant(idx, "sku", e.target.value)}
                                            className="w-32 bg-[#0A0A0B] border border-[#27272A] rounded-sm px-2 py-1 focus:border-[#00BCE5] outline-none"
                                        />
                                    </td>
                                    <td className="px-2 py-1.5">
                                        <input
                                            type="number"
                                            value={v.price}
                                            onChange={(e) => updateVariant(idx, "price", Number(e.target.value))}
                                            className="w-24 bg-[#0A0A0B] border border-[#27272A] rounded-sm px-2 py-1 focus:border-[#00BCE5] outline-none"
                                        />
                                    </td>
                                    <td className="px-2 py-1.5">
                                        <input
                                            type="number"
                                            value={v.sale_price ?? ""}
                                            onChange={(e) =>
                                                updateVariant(
                                                    idx,
                                                    "sale_price",
                                                    e.target.value === "" ? null : Number(e.target.value)
                                                )
                                            }
                                            className="w-24 bg-[#0A0A0B] border border-[#27272A] rounded-sm px-2 py-1 focus:border-[#00BCE5] outline-none"
                                        />
                                    </td>
                                    <td className="px-2 py-1.5">
                                        <input
                                            type="number"
                                            value={v.stock}
                                            onChange={(e) => updateVariant(idx, "stock", Number(e.target.value))}
                                            className="w-20 bg-[#0A0A0B] border border-[#27272A] rounded-sm px-2 py-1 focus:border-[#00BCE5] outline-none"
                                        />
                                    </td>
                                    <td className="px-2 py-1.5">
                                        <input
                                            type="checkbox"
                                            checked={!!v.active}
                                            onChange={(e) => updateVariant(idx, "active", e.target.checked)}
                                            className="accent-[#00BCE5]"
                                        />
                                    </td>
                                    <td className="px-2 py-1.5">
                                        <VariantImageCell
                                            url={v.image_url}
                                            onChange={(u) => updateVariant(idx, "image_url", u)}
                                        />
                                    </td>
                                    <td className="px-2 py-1.5 text-right">
                                        <button
                                            type="button"
                                            onClick={() => removeVariant(idx)}
                                            className="text-white/55 hover:text-[#FF3B30]"
                                            aria-label="Remove"
                                            data-testid={`variant-remove-${idx}`}
                                        >
                                            <Trash2 size={14} />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

function VariantImageCell({ url, onChange }) {
    const inputRef = useRef(null);
    const [busy, setBusy] = useState(false);
    const handleFile = async (file) => {
        if (!file) return;
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/admin/uploads", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            onChange(data.url);
        } catch (e) {
            // surface lightly
            onChange(url);
        }
        setBusy(false);
    };
    return (
        <div className="flex items-center gap-2">
            <div className="w-10 h-10 bg-[#0A0A0B] border border-[#27272A] rounded-sm overflow-hidden flex items-center justify-center">
                {url ? (
                    <img src={imageUrl(url)} alt="" className="w-full h-full object-cover" />
                ) : (
                    <ImageIcon size={14} className="text-white/35" />
                )}
            </div>
            <button
                type="button"
                onClick={() => inputRef.current?.click()}
                disabled={busy}
                className="text-[10px] uppercase tracking-widest font-display font-semibold border border-[#27272A] rounded-sm px-2 py-1 hover:border-[#00BCE5] hover:text-[#00BCE5] disabled:opacity-50"
                data-testid="variant-image-upload"
            >
                {busy ? "…" : url ? "Change" : "Upload"}
            </button>
            {url && (
                <button
                    type="button"
                    onClick={() => onChange("")}
                    className="text-[10px] uppercase tracking-widest font-display font-semibold text-white/55 hover:text-[#FF3B30]"
                    title="Clear variant image"
                    data-testid="variant-image-clear"
                >
                    Clear
                </button>
            )}
            <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} />
        </div>
    );
}
